// `/account` — the prototype's account screen, replaced by the one thing that actually has to happen here.
//
// THE PROTOTYPE'S ACCOUNT SCREEN IS GONE. It collected a name and an email and promised "No password.
// We'd email you a link to sign in", and no email-link or passkey auth exists in either repo
// (WEB_PATHWAY_INVENTORY.md §5b row 11, FALSE). It also promised "your appeal is here when you come
// back, and we can remind you before next year's deadline" — appeals are local-first on iOS and no
// reminder sender exists (§5b row 10, FALSE). Identity on web is anonymous auth at launch, as on iOS,
// so there is no account to create.
//
// WHAT REPLACES IT is the surface the prototype was MISSING entirely: Terms and Privacy acceptance,
// recorded through the version machinery (§6 row 1, MISSING). The texts, their versions and their
// hashes come from `v2Credits` `get`; the act is recorded by `v2Credits` `assent` citing the hash of
// the exact document that was shown. THE CLIENT HOLDS NO COPY of either document — that is
// `functions/src/legalVersions.ts`'s first rule, and it is why the links point at the served URLs.
//
// THE CHECKBOX SENTENCE AND THE STANDING DISCLOSURE ARE THE APP'S, HELD HERE (WEB-COPY-1). No endpoint serves either, so
// they were TODO(copy) placeholders until Tyler approved them for the website on 2026-09-29 ("approve all sentences..",
// items 1 and 2). They are `heldCopy.ts` entries, pinned to the Swift they copy; the checkbox sentence keeps its
// PROVISIONAL PENDING COUNSEL marker (R19) there. The assent record still cites the TERMS and PRIVACY hashes, as the
// app's does — the checkbox sentence is what the owner ticks, not a document that is hashed.
//
// WEB-PARITY-2B: THE APP'S ONBOARDING CONSENT (OnboardingView.swift:436-640; `docs/WEB_APP_PARITY.md` §2.5). The two
// document links are INSIDE the checkbox sentence, found in it by range lookup exactly as `AssentCopy.
// onboardingCheckboxAttributed` finds them, so a link can never name words the sentence does not contain (AC-3); the
// separate "The documents" list is gone, its version and effective date kept on each link's element. "What PROppeal is —
// and isn't" opens on a tap to the app's six points and the entity line (AC-4), above the standing disclosure the app
// always shows. The button is the app's "Get started", and until the box is ticked the page says the app's own hint for
// it, "Tick the box above to continue" (AC-5). Every one of those words is the app's, from `components/prePayCopy.ts`.
//
// WEB-DESIGN-1 (Tyler's walk decision 2, 2026-10-05): "Skip the separate terms page for owners who agreed at sign-up
// (A7/A8); keep 'What PROppeal is — and isn't' as a small 'Read' link by the buy button. No legal wording changes." The
// SERVER says whether the consent is on file (`legal.consentOnFile`, contract 2.24.0: its WEB_ACCOUNTS_ENABLED on, an
// account, the current versions agreed). `skipWhenOnFile` is passed only by `/account` for a signed-in owner with the
// switch on (`account/AccountDoor.tsx`); with it, consent on file goes straight to `/checkout`. Everywhere else — the
// switch off, a look-around, no consent, an older version — this page is exactly as it was. The disclosure row is
// `FinePrint`, the same component `/checkout` shows by its buy buttons, so both open the same approved text.


import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useId, useState } from "react";
import type { Copy } from "@contract";
import { useSession } from "../app/session";
import { useAction, useCall } from "../app/useCall";
import { CopyLine, CopyNote, HeldLine } from "../components/copy";
import { heldText } from "../components/heldCopy";
import { Actions } from "../components/layout";
import { PRIVACY_PHRASE, sentenceWithLinks, TERMS_PHRASE } from "../components/legalLinks";
import { ASSENT_POINTS, entityLine, prePayText } from "../components/prePayCopy";
import { Refusal } from "../components/Refusal";
import { CONTRACT_VERSION } from "@contract";
import { checkboxSha256 } from "../app/shownCopy";

/**
 * THE BUILD THAT RECORDED AN ACT (R-pending-ack-retention, P5): `web/<contract version>+<commit>`, stamped on every assent
 * and attestation this website records — it was the placeholder "web/0.0.0" until 2026-10-14, so a record could not say
 * which website build showed its words. The commit is the eight-character `git` hash `vite.config.ts` reads at build time
 * (`__PROPPEAL_COMMIT__`), "unknown" where git could not answer. 32 characters at most, the record's own limit.
 */
export const APP_VERSION = `web/${CONTRACT_VERSION}+${typeof __PROPPEAL_COMMIT__ === "string" ? __PROPPEAL_COMMIT__ : "unknown"}`;


/**
 * THE DISCLOSURE ROW (OnboardingView.swift:444-490): "What PROppeal is — and isn't" and "Read"/"Hide"; the six points and
 * the entity line one tap away, unchanged. Shown here and, since WEB-DESIGN-1, by `/checkout`'s buy buttons.
 */
export function FinePrint({ className = "card fineprint" }: { className?: string }) {
  const [open, setOpen] = useState(false);
  const bodyId = useId();
  return (
    <div className={className} data-fineprint>
      <button
        type="button"
        className="fineprint-row"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => setOpen((was) => !was)}
      >
        <span data-app-copy="assent.finePrint.title">{prePayText("assent.finePrint.title")}</span>
        <span className="fineprint-toggle" data-app-copy={open ? "assent.finePrint.hide" : "assent.finePrint.read"}>
          {prePayText(open ? "assent.finePrint.hide" : "assent.finePrint.read")}
        </span>
      </button>
      <div id={bodyId} className="fineprint-body" hidden={!open}>
        {ASSENT_POINTS.map((id) => (
          <p key={id} data-app-copy={id}>
            {prePayText(id)}
          </p>
        ))}
        <p className="faint" data-app-copy="assent.entity">
          {entityLine()}
        </p>
      </div>
    </div>
  );
}

/** R-pending-consent-gate: the server's sentence when it refused a purchase or a spend for the terms (CONSENT_REQUIRED),
 *  carried here in the router's state by `/checkout` (`ConsentRoute`), or null. Read safely: only a code and a text. */
function consentRefusalOf(state: unknown): Copy | null {
  const copy = (state as { consentRefusal?: unknown } | null)?.consentRefusal as { code?: unknown; text?: unknown } | undefined;
  return copy !== undefined && typeof copy.code === "string" && typeof copy.text === "string"
    ? { code: copy.code, text: copy.text }
    : null;
}

export function Assent({ skipWhenOnFile = false }: { skipWhenOnFile?: boolean } = {}) {
  const session = useSession();
  const navigate = useNavigate();
  // The server refused for the terms: say why, in its words, and never skip this page on a `get` that disagrees with it.
  const refused = consentRefusalOf(useLocation().state);
  const [agreed, setAgreed] = useState(false);
  const hintId = useId();
  const state = useCall(() => session.transport.v2Credits.get(), [session.transport, session.revision]);
  const record = useAction(async (args: { termsSha256: string; privacySha256: string; termsVersion: number; privacyVersion: number }) =>
    session.transport.v2Credits.assent({
      kind: "onboardingTerms",
      ...args,
      clientTimestampMs: Date.now(),
      appVersion: APP_VERSION,
      ...(await checkboxSha256()),
    }),
  );

  if (state.status === "pending") {
    return (
      <p className="spinner-line" role="status">
        Loading the current terms…
      </p>
    );
  }
  if (state.status === "failed") return <Refusal failure={state.failure} context="The terms" />;

  const legal = state.response.body.legal;
  // The server's answer, read once it has answered; never this page's own reading of versions (the thin-client rule).
  if (skipWhenOnFile && legal.consentOnFile && refused === null) return <Navigate to="/checkout" replace />;

  return (
    <>
      <p className="eyebrow">Before you pay</p>
      <h1>The terms you're agreeing to.</h1>
      {refused !== null && <CopyNote copy={refused} tone="warn" />}

      {legal.changeSummary.length > 0 && (
        <div className="card">
          <h3>What changed in the Terms</h3>
          {legal.changeSummary.map((line) => (
            <CopyLine key={line.code} copy={line} />
          ))}
        </div>
      )}
      {legal.privacyChangeSummary.length > 0 && (
        <div className="card">
          <h3>What changed in the Privacy Policy</h3>
          {legal.privacyChangeSummary.map((line) => (
            <CopyLine key={line.code} copy={line} />
          ))}
        </div>
      )}

      {/* THE DISCLOSURE ROW (OnboardingView.swift:444-490): one tap from the six points and the entity line. */}
      <FinePrint />

      {/* THE STANDING DISCLOSURE: the app's onboarding summary, the words Tyler wrote (item 2). */}
      <HeldLine id="assent.standingDisclosure" className="muted" />

      <label className="toggle">
        <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.currentTarget.checked)} />
        <span>
          {/* THE CHECKBOX SENTENCE: the app's `AssentCopy.onboardingCheckbox` (item 1), pending counsel (R19), with its two
              links inside it. No "recorded against version… by the hash" line (WEB-PARITY-1B, AC-2): the app shows none.
              The versions and hashes are still what the act records. */}
          <span className="consent" data-held-copy="assent.onboardingCheckbox">
            {sentenceWithLinks(heldText("assent.onboardingCheckbox"), [
              { phrase: TERMS_PHRASE, doc: legal.terms },
              { phrase: PRIVACY_PHRASE, doc: legal.privacy },
            ])}
          </span>
        </span>
      </label>

      {record.state.status === "failed" && <Refusal failure={record.state.failure} context="Recording your agreement" />}

      <Actions>
        <button
          className="btn primary"
          type="button"
          disabled={!agreed || record.state.status === "pending"}
          aria-describedby={agreed ? undefined : hintId}
          data-app-copy="assent.getStarted"
          onClick={async () => {
            const result = await record.run({
              termsSha256: legal.terms.sha256,
              privacySha256: legal.privacy.sha256,
              termsVersion: legal.terms.version,
              privacyVersion: legal.privacy.version,
            });
            if (result.ok) navigate("/checkout");
          }}
        >
          {prePayText("assent.getStarted")}
        </button>
        {!agreed && (
          <span id={hintId} className="faint" data-app-copy="assent.tickFirst">
            {prePayText("assent.tickFirst")}
          </span>
        )}
      </Actions>
    </>
  );
}
